// AnimatedCounter — count-up number display.
//
// 2026-07-02 rewrite: the original (Lovable-copied) implementation drove the
// number with framer-motion's useSpring({ duration: duration * 1000 }) — under
// framer-motion v12 that tween effectively never progresses (unit mismatch),
// so every consumer (PageStatsRow, dashboard tiles/gauges) rendered a
// permanent 0 while surrounding computed captions used the real value. Now a
// deterministic requestAnimationFrame ease-out tween with the same public
// API. Respects prefers-reduced-motion (jumps straight to the value).

import { useEffect, useRef, useState } from 'react';

interface AnimatedCounterProps {
  value: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  /** Animation length in seconds. */
  duration?: number;
}

const AnimatedCounter = ({
  value,
  decimals = 1,
  prefix = '',
  suffix = '',
  className = '',
  duration = 0.8,
}: AnimatedCounterProps) => {
  const [display, setDisplay] = useState(0);
  const currentRef = useRef(0);

  useEffect(() => {
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || duration <= 0) {
      currentRef.current = value;
      setDisplay(value);
      return;
    }
    const from = currentRef.current;
    const start = performance.now();
    let raf = 0;
    const tick = (t: number): void => {
      const progress = Math.min((t - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      const next = from + (value - from) * eased;
      currentRef.current = next;
      setDisplay(next);
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return (
    <span className={`font-mono ${className}`}>
      {`${prefix}${display.toFixed(decimals)}${suffix}`}
    </span>
  );
};

export default AnimatedCounter;
